import { BackofficeCustomerCesuContractsIndexResponse } from '../json-schemas/serializers/backoffice-customer-cesu-contracts-index-response'
import { BackofficeCesuContractsCountPerScopeResponse } from '../json-schemas/serializers/backoffice-cesu-contracts-count-per-scope-response'
import { CesuContractBase } from '../json-schemas/base/cesu_contract'
import { CesuRegistrationPayloadBase } from '../json-schemas/base/cesu_registration_payload'
import { CustomerBase } from '../json-schemas/base/customer'
import { EmployeeBase } from '../json-schemas/base/employee'
import { httpGet, httpGetRoute, httpPatch, httpPost, httpPostMultipart } from '../utils/http.service'
import { I18nService } from '../utils/i18n.service'
import CesuContractJobQualificationSchema from '../../../../../../json_schemas/cesu_contract/job-qualification.json'
import i18nScopedData from './cesu-contracts.service.yml'
import { toUpperCaseFirst } from '../utils/lodash.service'

const i18nScoped = new I18nService(i18nScopedData)
export const cesuContractJobQualifications = CesuContractJobQualificationSchema.enum as CesuContractBase['job_qualification'][]
export const cesuContractContractTypes: CesuContractBase['contract_type'][] = ['DSN_01', 'DSN_02']

export const cesuContractChargeExemptions: CesuContractBase['charge_exemption'][] = ['02', '01', '03', '05', '06', '70', '71', '80']
export type CesuContractScope = 'draft' | 'awaiting_customer_signature' | 'awaiting_employee_signature' | 'validated_with_signature' | 'validated_skip_signing' | 'cesu_plus_not_requested' | 'certificate_to_sign' | 'certificate_validated_in_progress' | 'certificate_validated_error' | 'certificate_validated_success' | 'all'

export interface RansackQuery {
  'q[customer_id_eq]'?: CustomerBase['id']
  'q[employee_id_eq]'?: EmployeeBase['id']
  'q[id_not_in][]'?: number[]
  from_date?: string
  to_date?: string
  scope?: CesuContractScope
}

export function cesuContractsList (query: RansackQuery): Promise<BackofficeCustomerCesuContractsIndexResponse> {
  return httpGet(`/${httpGetRoute()}/cesu_contracts`, query)
}

export function cesuContractCountPerScope (query: RansackQuery): Promise<BackofficeCesuContractsCountPerScopeResponse> {
  return httpGet(`/${httpGetRoute()}/cesu_contracts/count_per_scope`, query)
}

export function cesuContractI18nScope (scope: CesuContractScope): string {
  return i18nScoped.t(`scope.${scope}`)
}

export function cesuContractsCreate (body: Partial<CesuContractBase>): Promise<void> {
  return httpPost(`/${httpGetRoute()}/cesu_contracts`, { cesu_contract: body })
}

export function cesuContractsUpdate (body: any): Promise<void> {
  return httpPatch(`/${httpGetRoute()}/cesu_contracts/${body.id}`, { cesu_contract: body })
}

export function cesuContractEstimateContributionPercentages (params: Pick<CesuContractBase, 'estimated_monthly_hours' | 'raw_employee_hourly_salary_cents' | 'charge_exemption'>): Promise<{ employer_charge_percentage: number; employee_charge_percentage: number }> {
  return httpGet(`/${httpGetRoute()}/cesu_contracts/estimate_contribution_percentages`, params)
}

export function cesuContractUploadDocument (id: CesuContractBase['id'], file: Blob, variant: 'contract' | 'certificate'): Promise<void> {
  const formData = new FormData()
  formData.append('file', file)
  return httpPostMultipart(`/${httpGetRoute()}/cesu_contracts/${id}/upload_${variant}`, formData) as Promise<void>
}

export function cesuContractSendContract (id: CesuContractBase['id'], alsoSendCertificate: boolean): Promise<void> {
  return httpPost(`/${httpGetRoute()}/cesu_contracts/${id}/send_contract`, { also_send_certificate: alsoSendCertificate }) as Promise<void>
}

export function cesuContractSendCertificate (id: CesuContractBase['id']): Promise<void> {
  return httpPost(`/${httpGetRoute()}/cesu_contracts/${id}/send_certificate`, {}) as Promise<void>
}


export function cesuContractI18nChargeExemption (code: CesuContractBase['charge_exemption'], { capitalize = true } = {}): string {
  const translated = i18nScoped.t(`charge_exemption.${code}`)
  return capitalize ? toUpperCaseFirst(translated) : translated
}

export function cesuContractI18nContractType (value: CesuContractBase['contract_type']): string {
  return i18nScoped.t(`contract_type.${value}`)
}

export function cesuContractI18nJobQualification (value: CesuContractBase['job_qualification']): string {
  return i18nScoped.t(`job_qualification.${value}`)
}

export function cesuContractI18nOverworkPaid (paid: boolean): string {
  return i18nScoped.t(`overwork_paid.${paid}`)
}

export function cesuContractI18nRestWeekDayWorkedResolution (paid: boolean, short = false): string {
  return i18nScoped.t(`rest_week_day_worked_paid.${paid}${short ? '_short' : ''}`)
}

type CesuContractStateObject = { text: string, colorClassName: string, iconClassName: string }

export function cesuContractStateAsObject (state: CesuContractBase['state'], cesuContract?: Pick<CesuContractBase, 'skip_signing'>, verbose = false): CesuContractStateObject {
  const prefix = verbose ? 'state_verbose' : 'state'
  const text = cesuContract?.skip_signing && state === 'validated'
    ? i18nScoped.t(`${prefix}.validated_no_signing`)
    : i18nScoped.t(`${prefix}.${state}`)
  switch (state) {
    case 'draft': return { text, colorClassName: 'u-txtClr2', iconClassName: 'icon-edit' }
    case 'created': return { text, colorClassName: 'u-txtClr3', iconClassName: 'icon-clock' }
    case 'partially_signed': return { text, colorClassName: 'u-txtClr3', iconClassName: 'icon-clock' }
    case 'validated': return { text, colorClassName: 'u-txtClrSuccess', iconClassName: 'icon-check' }
  }
}

export function cesuContractStateCertificateAsObject (
  state: CesuContractBase['state_certificate'],
  cesuRegistrationPayload?: Pick<CesuRegistrationPayloadBase, 'payslip_mandate_sent_at' | 'payslip_mandate_failure_code' | 'payslip_mandate_failure_message' | 'aici_enabled_at' | 'aici_enabling_failure_code' | 'aici_enabling_failure_message'> | null,
  cesuContract?: Pick<CesuContractBase, 'cesu_plus_enabled_at' | 'cesu_plus_enabling_failure_code' | 'cesu_plus_enabling_failure_message'> | null
): CesuContractStateObject {
  if (state === 'validated' && cesuRegistrationPayload) {
    if (cesuRegistrationPayload.payslip_mandate_failure_code || cesuRegistrationPayload.payslip_mandate_failure_message) {
      return { text: i18nScoped.t('state_certificate.validated_mandate_failed'), colorClassName: 'u-txtClrError', iconClassName: 'icon-remove' }
    }

    if (!cesuRegistrationPayload.payslip_mandate_sent_at) {
      return { text: i18nScoped.t('state_certificate.validated_mandate_pending'), colorClassName: 'u-txtClr3', iconClassName: 'icon-clock' }
    }

    if (cesuRegistrationPayload.aici_enabling_failure_code || cesuRegistrationPayload.aici_enabling_failure_message) {
      return { text: i18nScoped.t('state_certificate.validated_aici_failed'), colorClassName: 'u-txtClrError', iconClassName: 'icon-remove' }
    }

    if (!cesuRegistrationPayload.aici_enabled_at) {
      return { text: i18nScoped.t('state_certificate.validated_aici_pending'), colorClassName: 'u-txtClr3', iconClassName: 'icon-clock' }
    }

    if (cesuContract?.cesu_plus_enabling_failure_code || cesuContract?.cesu_plus_enabling_failure_message) {
      return { text: i18nScoped.t('state_certificate.validated_cesu_plus_failed'), colorClassName: 'u-txtClrError', iconClassName: 'icon-remove' }
    }

    if (!cesuContract?.cesu_plus_enabled_at) {
      return { text: i18nScoped.t('state_certificate.validated_cesu_plus_pending'), colorClassName: 'u-txtClr3', iconClassName: 'icon-clock' }
    }
  }

  if (state === 'draft') return { text: i18nScoped.t('state_certificate.draft'), colorClassName: 'u-txtClr2', iconClassName: 'icon-remove' }
  if (state === 'created') return { text: i18nScoped.t('state_certificate.created'), colorClassName: 'u-txtClr3', iconClassName: 'icon-clock' }
  if (state === 'partially_signed') return { text: i18nScoped.t('state_certificate.partially_signed'), colorClassName: 'u-txtClr3', iconClassName: 'icon-clock' }
  if (state === 'validated') return { text: i18nScoped.t('state_certificate.validated'), colorClassName: 'u-txtClrSuccess', iconClassName: 'icon-check' }
  throw new Error(`Implemented state_certificate = ${state}`)
}
